import React from 'react';
import { NextUI } from '@/components/NextUI';
import UploadIt from './UploadIt';
import TypeIt from './TypeIt';
import DrawIt from './DrawIt';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from '@heroui/dropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'next-intl';

const YourSign = ({
  onCallback,
  hideModal,
  data,
}: {
  onCallback: (key: string, data: any) => void;
  hideModal: any;
  data: {
    type: string;
  };
}) => {
  const [activeTab, setActiveTab] = React.useState('draw');
  const signRef = React.useRef<HTMLCanvasElement>();
  const [hasSignature, setHasSignature] = React.useState(false);
  const t = useTranslations('modals.your_sign');

  const tabItems = [
    { id: 'draw', label: 'Draw' },
    { id: 'type', label: 'Type' },
    { id: 'upload', label: 'Upload' },
  ];

  const handleSignatureChange = (hasContent: boolean) => {
    setHasSignature(hasContent);
  };

  const handleSign = () => {
    if (hasSignature && signRef.current) {
      const canvas = signRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const { left, top, width, height } = getSignatureBounds(canvas);

      const croppedCanvas = document.createElement('canvas');
      croppedCanvas.width = width;
      croppedCanvas.height = height;
      const croppedCtx = croppedCanvas.getContext('2d');
      if (!croppedCtx) return;

      croppedCtx.drawImage(
        canvas,
        left,
        top,
        width,
        height,
        0,
        0,
        width,
        height
      );

      if (data?.type === 'initial') {
        onCallback('yourInitialDataUrl', croppedCanvas.toDataURL());
      } else {
        onCallback('eSignDataUrl', croppedCanvas.toDataURL());
      }

      hideModal?.();
    }
  };

  const getSignatureBounds = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return { left: 0, top: 0, width: 0, height: 0 };

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    let minX = canvas.width;
    let minY = canvas.height;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const alpha = data[(y * canvas.width + x) * 4 + 3];
        if (alpha > 0) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    return {
      left: minX,
      top: minY,
      width: maxX - minX + 1,
      height: maxY - minY + 1,
    };
  };

  return (
    <NextUI.Modal
      className="max-w-[872px] !select-none"
      classNames={{ content: 'py-8 !px-4 lg:!px-6' }}
      defaultOpen={true}
      name={'YourSign'}
    >
      <div className="flex justify-between flex-col md:flex-row w-full">
        <div className="hidden md:flex md:flex-col">
          {tabItems.map((tab) => (
            <div
              key={tab.id}
              className={`px-4 py-6 w-[198px] rounded-[10px] cursor-pointer ${
                activeTab === tab.id ? 'bg-neutral-100' : ''
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span
                className={`text-[17px] ${
                  activeTab === tab.id ? 'text-dark' : 'text-gray-500'
                }`}
              >
                {tab.label}
              </span>
            </div>
          ))}
        </div>

        <div className="w-full flex justify-start md:hidden mt-6">
          <Dropdown className="!min-w-0 !max-w-[150px]">
            <DropdownTrigger className="!min-w-0 !px-4">
              <NextUI.Button variant={'ghost'} className="!border-0">
                <span className="text-[17px]">
                  {tabItems.find((tab) => tab.id === activeTab)?.label}
                </span>
                <FontAwesomeIcon
                  icon={['fas', 'chevron-down']}
                  className="text-xs text-gray-medium"
                />
              </NextUI.Button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Signature Colors">
              {tabItems.map((tab) => (
                <DropdownItem
                  key={tab.id}
                  className={`px-4 py-3 cursor-pointer hover:!bg-neutral-100 ${
                    activeTab === tab.id ? 'bg-neutral-100' : ''
                  }`}
                  onClick={() => {
                    setActiveTab(tab.id);
                  }}
                >
                  <span
                    className={`text-[17px] ${
                      activeTab === tab.id ? 'text-dark' : 'text-gray-medium'
                    }`}
                  >
                    {tab.label}
                  </span>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>

        <div className="flex-1">
          {activeTab === 'draw' && (
            <DrawIt
              signRef={signRef as any}
              onSignatureChange={handleSignatureChange}
            />
          )}
          {activeTab === 'type' && (
            <TypeIt
              signRef={signRef as any}
              onSignatureChange={handleSignatureChange}
            />
          )}
          {activeTab === 'upload' && (
            <UploadIt
              signRef={signRef as any}
              onSignatureChange={handleSignatureChange}
            />
          )}
        </div>
      </div>

      <div className="flex flex-col gap-8">
        <div className="flex flex-row justify-end gap-5">
          <NextUI.Button
            className="!w-[150px]"
            variant="outline"
            onClick={hideModal}
          >
            {t('buttons.cancel')}
          </NextUI.Button>
          <NextUI.Button
            className="!w-[150px]"
            onClick={handleSign}
            disabled={!hasSignature}
          >
            {t('buttons.create')}
          </NextUI.Button>
        </div>
      </div>
    </NextUI.Modal>
  );
};

export default YourSign;
